<script setup>
import { computed, h, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOperators } from 'dashboard/components-next/filter/operators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AutomationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';
import WorkflowTriggerConditions from './WorkflowTriggerConditions.vue';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import {
  getAttributes,
  getFileName,
  showActionInput,
} from 'dashboard/helper/automationHelper';
import { AUTOMATION_INBOX_ONLY_CONDITION_EVENT_KEYS } from './constants';
import {
  getWorkflowActionDisplayGroup,
  getWorkflowActionNodeTitle,
  getWorkflowActionStepDescriptionI18nKeys,
  getWorkflowActionStepVisual,
  getWorkflowTriggerDescriptionI18nKeys,
  getWorkflowTriggerStepVisual,
} from './workflowCanvasStepTheme';
import WorkflowTriggerEventSelect from './WorkflowTriggerEventSelect.vue';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: value => ['create', 'edit'].includes(value),
  },
  activePanel: {
    type: String,
    required: true,
  },
  automationTypes: {
    type: Object,
    required: true,
  },
  automationRuleEvents: {
    type: Array,
    required: true,
  },
  automationActionTypes: {
    type: Array,
    required: true,
  },
  getConditionDropdownValues: {
    type: Function,
    required: true,
  },
  getActionDropdownValues: {
    type: Function,
    required: true,
  },
  appendNewCondition: {
    type: Function,
    required: true,
  },
  removeFilter: {
    type: Function,
    required: true,
  },
  removeAction: {
    type: Function,
    required: true,
  },
  resetAction: {
    type: Function,
    required: true,
  },
  onEventChange: {
    type: Function,
    required: true,
  },
  errors: {
    type: Object,
    required: true,
  },
  isReadOnly: {
    type: Boolean,
    default: false,
  },
});

const automation = defineModel('automation', { type: Object, default: null });

const INPUT_TYPE_MAP = {
  multi_select: 'multiSelect',
  search_select: 'searchSelect',
  plain_text: 'plainText',
  comma_separated_plain_text: 'plainText',
  date: 'date',
};

const { t } = useI18n();
const { operators } = useOperators();

const isEditMode = computed(() => props.mode === 'edit');

const getTranslatedAttributes = (type, event) => {
  return getAttributes(type, event).map(attribute => {
    const skipTranslation =
      attribute.customAttributeType ||
      ['contact_custom_attribute', 'conversation_custom_attribute'].includes(
        attribute.key
      );
    return {
      ...attribute,
      name: skipTranslation
        ? attribute.name
        : t(`AUTOMATION.ATTRIBUTES.${attribute.name}`),
    };
  });
};

const eventName = computed(() => automation.value?.event_name);

const triggerDescription = computed(() => {
  if (!eventName.value) return '';
  return t(
    `AUTOMATION.FLOW.TRIGGER_DESCRIPTIONS.${eventName.value.toUpperCase()}`
  );
});

const filterTypes = computed(() => {
  const event = eventName.value;
  if (!event || !props.automationTypes[event]) return [];

  const attributes = getTranslatedAttributes(props.automationTypes, event);

  return attributes.map(attr => {
    if (attr.disabled) {
      return { value: attr.key, label: attr.name, disabled: true };
    }

    const mappedInputType = INPUT_TYPE_MAP[attr.inputType] || 'plainText';
    const options = props.getConditionDropdownValues(attr.key) || [];

    const filterOperators = (attr.filterOperators || []).map(op => {
      const enriched = operators.value[op.value];
      if (enriched) return enriched;
      return {
        value: op.value,
        label: t(`FILTER.OPERATOR_LABELS.${op.value}`),
        hasInput: true,
        inputOverride: null,
        icon: h('span', { class: 'i-ph-equals-bold !text-n-blue-11' }),
      };
    });

    return {
      attributeKey: attr.key,
      value: attr.key,
      attributeName: attr.name,
      label: attr.name,
      inputType: mappedInputType,
      options,
      filterOperators,
      dataType: 'text',
      attributeModel: attr.customAttributeType || 'standard',
    };
  });
});

const hasAutomationMutated = computed(() => {
  const firstActionParams = automation.value?.actions[0]?.action_params;

  return Boolean(
    automation.value?.conditions[0]?.values ||
      firstActionParams?.length ||
      (firstActionParams &&
        typeof firstActionParams === 'object' &&
        Object.keys(firstActionParams).length)
  );
});

const hasConditionErrors = computed(() =>
  Object.keys(props.errors).some(key => key.startsWith('condition_'))
);

const isInboxOnlyTrigger = computed(() =>
  AUTOMATION_INBOX_ONLY_CONDITION_EVENT_KEYS.has(eventName.value)
);

const hasActionErrors = computed(() =>
  Object.keys(props.errors).some(key => key.startsWith('action_'))
);

const ASSIGN_TO_SUPPORTED_ACTION_KEYS = new Set([
  'assign_agent',
  'assign_ai_agent',
  'assign_team',
  'remove_assigned_agent',
]);
const DATE_TIME_SUPPORTED_ACTION_KEYS = new Set(['date_time_window']);

const getFilteredActionTypes = actionName => {
  if (actionName === 'date_time_window') {
    return props.automationActionTypes
      .filter(({ key }) => DATE_TIME_SUPPORTED_ACTION_KEYS.has(key))
      .map(action => ({
        ...action,
        disabled: false,
      }));
  }

  if (getWorkflowActionDisplayGroup(actionName) !== 'ASSIGN_TO') {
    return props.automationActionTypes;
  }

  return props.automationActionTypes
    .filter(({ key }) => ASSIGN_TO_SUPPORTED_ACTION_KEYS.has(key))
    .map(action => ({
      ...action,
      disabled: false,
    }));
};

const panelHeader = computed(() => {
  const p = props.activePanel;

  if (p === 'trigger-conditions') {
    const visual = getWorkflowTriggerStepVisual();
    const keys = getWorkflowTriggerDescriptionI18nKeys();
    return {
      title: t('AUTOMATION.FLOW.NODE_TRIGGER'),
      longDescription: t(keys.long),
      icon: visual.hugeIcon,
      iconClass: visual.iconClass,
    };
  }

  const m = /^action-(\d+)$/.exec(p);
  if (m) {
    const index = Number(m[1]);
    const action = automation.value?.actions?.[index];
    if (!action) return null;

    const visual = getWorkflowActionStepVisual(action.action_name);
    const keys = getWorkflowActionStepDescriptionI18nKeys(action.action_name);
    return {
      title: getWorkflowActionNodeTitle(
        action.action_name,
        automation.value.actions,
        index,
        t
      ),
      longDescription: t(keys.long),
      icon: visual.hugeIcon,
      iconClass: visual.iconClass,
    };
  }

  return null;
});

const activeActionIndex = computed(() => {
  const match = /^action-(\d+)$/.exec(props.activePanel);
  return match ? Number(match[1]) : null;
});

const isEditingStepTitle = ref(false);
const stepTitleDraft = ref('');

const startEditingStepTitle = () => {
  const index = activeActionIndex.value;
  if (index === null) return;

  stepTitleDraft.value =
    automation.value.actions[index].title ||
    getWorkflowActionNodeTitle(
      automation.value.actions[index].action_name,
      automation.value.actions,
      index,
      t
    );
  isEditingStepTitle.value = true;
};

const saveStepTitle = () => {
  const index = activeActionIndex.value;
  if (index === null) return;

  const title = stepTitleDraft.value.trim();
  if (title) {
    automation.value.actions[index].title = title;
  } else {
    delete automation.value.actions[index].title;
  }
  isEditingStepTitle.value = false;
};

const triggerConditionsRef = useTemplateRef('triggerConditionsRef');

const validateConditions = () =>
  triggerConditionsRef.value?.validate?.() ?? true;

const resetConditionRowValidation = () => {
  triggerConditionsRef.value?.resetValidation?.();
};

const syncCustomAttributeTypesForAutomation = () => {
  if (!automation.value?.conditions?.length) return;
  automation.value.conditions.forEach(condition => {
    const filterType = filterTypes.value.find(
      ft => ft.attributeKey === condition.attribute_key
    );
    condition.custom_attribute_type =
      filterType?.attributeModel === 'standard'
        ? ''
        : filterType?.attributeModel || '';
  });
};

defineExpose({
  validateConditions,
  resetConditionRowValidation,
  syncCustomAttributeTypesForAutomation,
});
</script>

<template>
  <div class="flex h-full min-h-0 w-full flex-col">
    <div
      v-if="automation"
      class="flex h-full min-h-0 w-full flex-col gap-4 bg-n-solid-2 p-4 dark:bg-n-solid-2"
    >
      <div
        v-if="panelHeader"
        class="min-h-0 shrink-0 border-b border-n-weak pb-4 dark:border-n-strong"
      >
        <div class="flex flex-col gap-1.5">
          <div class="flex items-center justify-between gap-3">
            <div class="flex min-w-0 items-center gap-3">
              <HugeIcon
                :icon="panelHeader.icon"
                :size="24"
                class="shrink-0"
                :class="panelHeader.iconClass"
              />
              <NextInput
                v-if="isEditingStepTitle"
                v-model="stepTitleDraft"
                size="sm"
                autofocus
                :aria-label="$t('AUTOMATION.ACTIONS.STEP_NAME')"
                :placeholder="$t('AUTOMATION.ACTIONS.STEP_NAME')"
                class="w-[19rem] max-w-full"
                @blur="saveStepTitle"
                @enter="saveStepTitle"
              />
              <h2
                v-else
                class="m-0 truncate text-lg font-semibold leading-snug text-n-slate-12"
              >
                {{ panelHeader.title }}
              </h2>
            </div>
            <div
              v-if="activeActionIndex !== null && !isReadOnly"
              class="flex shrink-0 items-center gap-1"
            >
              <NextButton
                sm
                ghost
                slate
                icon="i-lucide-pencil"
                :aria-label="$t('AUTOMATION.ACTIONS.RENAME_STEP')"
                class="!bg-transparent shadow-none hover:!bg-transparent focus:!bg-transparent active:!bg-transparent"
                @click="startEditingStepTitle"
              />
              <NextButton
                sm
                ghost
                slate
                icon="i-lucide-trash"
                :aria-label="$t('AUTOMATION.ACTIONS.DELETE_STEP')"
                class="!bg-transparent shadow-none hover:!bg-transparent focus:!bg-transparent active:!bg-transparent"
                @click="removeAction(activeActionIndex)"
              />
            </div>
          </div>
          <p class="m-0 text-[11px] leading-relaxed text-n-slate-12">
            {{ panelHeader.longDescription }}
          </p>
        </div>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto">
        <div
          v-show="activePanel === 'trigger-conditions'"
          class="flex flex-col gap-4"
        >
          <fieldset :disabled="isReadOnly" class="m-0 min-w-0 border-0 p-0">
            <div class="mb-3 flex flex-col gap-2">
              <div class="flex flex-col gap-1.5">
                <WorkflowTriggerEventSelect
                  v-model="automation.event_name"
                  :events="automationRuleEvents"
                  :has-error="Boolean(errors.event_name)"
                  :disabled="isReadOnly"
                  @change="onEventChange()"
                />
                <p
                  v-if="triggerDescription"
                  class="m-0 text-sm italic leading-snug text-n-slate-11"
                >
                  {{ triggerDescription }}
                </p>
                <span v-if="errors.event_name" class="message">
                  {{ $t('AUTOMATION.ADD.FORM.EVENT.ERROR') }}
                </span>
              </div>
              <p
                v-if="!isEditMode && hasAutomationMutated"
                class="m-0 text-[11px] leading-snug text-n-slate-12"
              >
                {{ $t('AUTOMATION.FORM.RESET_MESSAGE') }}
              </p>
            </div>

            <WorkflowTriggerConditions
              v-if="eventName"
              ref="triggerConditionsRef"
              :conditions="automation.conditions"
              :filter-types="filterTypes"
              :is-inbox-only="isInboxOnlyTrigger"
              :has-errors="hasConditionErrors"
              :is-read-only="isReadOnly"
              @append="appendNewCondition"
              @remove="removeFilter"
            />
          </fieldset>
        </div>

        <div
          v-for="(action, i) in automation.actions"
          v-show="activePanel === `action-${i}`"
          :key="`inspector-action-${i}`"
          class="flex h-full min-h-0 flex-col gap-2"
        >
          <ul
            class="grid h-full min-h-0 list-none p-0"
            :class="
              hasActionErrors && errors[`action_${i}`]
                ? 'bg-n-ruby-2/20 rounded-lg'
                : ''
            "
          >
            <AutomationActionInput
              v-model="automation.actions[i]"
              :action-types="getFilteredActionTypes(action.action_name)"
              dropdown-max-height="max-h-[7.5rem]"
              :dropdown-values="getActionDropdownValues(action.action_name)"
              :is-read-only="isReadOnly"
              :show-action-input="
                showActionInput(
                  getFilteredActionTypes(action.action_name),
                  action.action_name
                )
              "
              :error-message="
                errors[`action_${i}`]
                  ? $t(`AUTOMATION.ERRORS.${errors[`action_${i}`]}`)
                  : ''
              "
              :initial-file-name="
                isEditMode ? getFileName(action, automation.files) : ''
              "
              @reset-action="resetAction(i)"
            />
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
